import {
  BooleanFilter,
  CustomFilter,
  DateFilter,
  DateRangeFilter,
  Filter,
  FilterValue,
  MultiSelectFilter,
  SingleSelectFilter,
} from "../types";

/**
 * Returns an array of filters that are currently active (i.e., have a value set).
 *
 * @param {Filter[]} filters - Array of filter objects to check
 * @returns {Filter[]} Array of active filters
 */
export const getActiveFilters = (filters: Filter[]): Filter[] => {
  return filters.filter((filter) => {
    switch (filter.type) {
      case "boolean":
        return filter.checked;
      case "custom":
        return filter.value !== undefined;
      case "singleSelect":
        return filter.selectedItem !== undefined;
      case "multiSelect":
        return filter.selectedItems && filter.selectedItems.length > 0;
      case "date":
        return filter.value !== null && filter.value !== undefined;
      case "dateRange":
        return (
          filter.value !== null &&
          filter.value !== undefined &&
          filter.value.startDate !== null &&
          filter.value.endDate !== null
        );
      default:
        return false;
    }
  });
};

/**
 * Checks if any filters in the array have active values
 *
 * @param {Filter[]} filters - Array of filter objects to check
 * @returns {boolean} True if any filter has an active value
 */
export const checkActiveFilters = (filters: Filter[]): boolean => {
  return getActiveFilters(filters).length > 0;
};

/**
 * Resets all filters to their default state
 *
 * @param {Filter[]} filters - Array of filter objects to reset
 * @returns {Filter[]} New array with reset filters
 */
export const resetFilters = (filters: Filter[]): Filter[] => {
  return filters.map((filter) => {
    switch (filter.type) {
      case "boolean":
        return {
          ...filter,
          checked: false,
        };
      case "custom":
        return {
          ...filter,
          value: undefined,
        };
      case "singleSelect":
        return {
          ...filter,
          selectedItem: undefined,
        };
      case "multiSelect":
        return {
          ...filter,
          selectedItems: [],
        };
      case "date":
      case "dateRange":
        return {
          ...filter,
          value: null,
        };
      default:
        return filter;
    }
  });
};

/**
 * Updates a single filter in an array of filters
 *
 * @param {Filter[]} filters - Array of filter objects
 * @param {string} filterId - ID of the filter to update
 * @param {FilterValue} value - New value for the filter
 * @returns {Filter[]} New array with updated filter
 */
export const updateSingleFilter = (
  filters: Filter[],
  filterId: string,
  value: FilterValue,
): Filter[] => {
  return filters.map((filter) => {
    if (filter.id !== filterId) return filter;

    switch (filter.type) {
      case "boolean":
        return {
          ...filter,
          checked: value as boolean,
        };
      case "singleSelect":
        return {
          ...filter,
          selectedItem: value,
        };
      case "multiSelect":
        return {
          ...filter,
          selectedItems: value as unknown[],
        };
      case "date":
        return {
          ...filter,
          value: value as string | null,
        };
      case "dateRange":
        return {
          ...filter,
          value: value as {
            startDate: string | null;
            endDate: string | null;
          } | null,
        };
      case "custom":
        return {
          ...filter,
          value,
        };
      default:
        return filter;
    }
  });
};

/**
 * Determines if two filter values are different.
 * For arrays (multiSelect), compares by id set (order-insensitive, avoids JSON serialization).
 * For singleSelect, compares by id if both are objects with id.
 *
 * @param {T} a - First value to compare
 * @param {T} b - Second value to compare
 * @returns {boolean} True if the values are different
 */
export const hasChangedFilter = <T>(a: T, b: T): boolean => {
  // For arrays (multiSelect), compare by id set to handle new array references with same items.
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return true;
    if (a.length === 0) return false;
    const aItems = a as Array<{ id?: unknown }>;
    const bItems = b as Array<{ id?: unknown }>;
    if (aItems[0] && "id" in aItems[0]) {
      const aIds = new Set(aItems.map((item) => item.id));
      return (bItems as Array<{ id: unknown }>).some(
        (item) => !aIds.has(item.id),
      );
    }
    return JSON.stringify(a) !== JSON.stringify(b);
  }
  // For singleSelect, compare by id if both are objects with id
  if (
    a &&
    b &&
    typeof a === "object" &&
    typeof b === "object" &&
    "id" in a &&
    "id" in b
  ) {
    return a.id !== b.id;
  }
  // Fallback to strict equality
  return a !== b;
};

/**
 * Gets the selection value from a filter based on its type.
 * Internal helper function used by hasFilterSelectionChanged.
 *
 * @param {Filter} filter - The filter to extract the selection value from
 * @returns {unknown} The selection value (checked, selectedItem, selectedItems, value)
 */
const getFilterSelectionValue = (filter: Filter): unknown => {
  switch (filter.type) {
    case "boolean":
      return filter.checked;
    case "singleSelect":
      return filter.selectedItem;
    case "multiSelect":
      return filter.selectedItems;
    case "date":
    case "dateRange":
    case "custom":
      return filter.value;
    default:
      return undefined;
  }
};

/**
 * Checks if the selection value of a filter has changed by comparing references.
 * Uses reference equality (===) to detect if the parent has provided a new value.
 *
 * @param {Filter} existingFilter - The current filter state
 * @param {Filter} newFilter - The incoming filter from props
 * @returns {boolean} True if the selection value reference has changed
 */
export const hasFilterSelectionChanged = (
  existingFilter: Filter,
  newFilter: Filter,
): boolean => {
  const existingValue = getFilterSelectionValue(existingFilter);
  const newValue = getFilterSelectionValue(newFilter);

  // Reference equality check - returns true if references differ
  return existingValue !== newValue;
};

/**
 * Preserves the selection state from an existing filter into a new filter.
 * Used when filter configuration updates but the selection value reference hasn't changed,
 * allowing us to maintain user selections through configuration updates.
 *
 * @param {Filter} newFilter - The new filter configuration from props
 * @param {Filter} existingFilter - The existing filter with user's selections
 * @returns {Filter} New filter with configuration from newFilter and selection from existingFilter
 */
export const preserveFilterState = (
  newFilter: Filter,
  existingFilter: Filter,
): Filter => {
  switch (newFilter.type) {
    case "boolean":
      return {
        ...newFilter,
        checked: (existingFilter as BooleanFilter).checked,
      };
    case "singleSelect":
      return {
        ...newFilter,
        selectedItem: (existingFilter as SingleSelectFilter<unknown>)
          .selectedItem,
      };
    case "multiSelect":
      return {
        ...newFilter,
        selectedItems: (existingFilter as MultiSelectFilter<unknown>)
          .selectedItems,
      };
    case "date":
      return {
        ...newFilter,
        value: (existingFilter as DateFilter).value,
      };
    case "dateRange":
      return {
        ...newFilter,
        value: (existingFilter as DateRangeFilter).value,
      };
    case "custom":
      return {
        ...newFilter,
        value: (existingFilter as CustomFilter<unknown>).value,
      };
    default:
      return newFilter;
  }
};

/**
 * Creates a deep clone of filters with updated item references.
 * Ensures that selected items reference the current items array for proper equality checks.
 * Used when initializing draft state to ensure selections use current item references.
 *
 * @param {Filter[]} filters - Array of filter objects to clone
 * @returns {Filter[]} New array with cloned filters and updated item references
 */
export const cloneFiltersWithItemRefs = (filters: Filter[]): Filter[] => {
  return filters.map((filter) => {
    switch (filter.type) {
      case "singleSelect":
        return {
          ...filter,
          selectedItem: filter.selectedItem
            ? filter.items.find(
                (item) => item.id === filter.selectedItem?.id,
              ) || filter.selectedItem
            : undefined,
        };
      case "multiSelect":
        return {
          ...filter,
          selectedItems: filter.selectedItems
            ? filter.selectedItems.map(
                (selectedItem) =>
                  filter.items.find((item) => item.id === selectedItem.id) ||
                  selectedItem,
              )
            : [],
        };
      case "date":
        return {
          ...filter,
          value: filter.value as string | null,
        };
      case "dateRange":
        return {
          ...filter,
          value: filter.value as {
            startDate: string | null;
            endDate: string | null;
          } | null,
        };
      case "custom": {
        const { value } = filter;

        if (value === undefined) {
          return { ...filter, value: undefined };
        }
        if (Array.isArray(value)) {
          return { ...filter, value: [...value] };
        }
        if (typeof value === "object" && value !== null) {
          return { ...filter, value: { ...value } };
        }
        return { ...filter, value };
      }
      default:
        return { ...filter };
    }
  });
};
